/**
 * Vista: Gestión de Catálogo de Almacenes
 * ERP F1 (B1, diseño §2.7): almacén con TIPO (propio, consigna, tránsito, garantías, demo, interno), custodio
 * (consigna = distribuidor que tiene la mercancía), principal, permite venta, ciudad y estado. La baja ya no borra.
 * Patrón de 2 pestañas (utils/pestanasErp.js): «Consultar» (indicadores, filtros y listado) y «Agregar / editar».
 */

import almacenService from '../services/almacen.service.js';
import api from '../services/api.js';
import permisos from '../services/permissions.service.js';
import modal from '../utils/modal.js';
import toast from '../utils/toast.js';
import { esc, errMsg, badge, val } from './catalogos/erpUi.js';
import { permisosPantalla, pestanasHtml, Pestanas } from '../utils/pestanasErp.js';

const TIPOS = {
  propio: ['Propio', 'success'],
  consigna: ['Consigna', 'info'],
  transito: ['Tránsito', 'warning'],
  garantias: ['Garantías', 'danger'],
  demo: ['Demo', 'info'],
  interno: ['Interno', 'warning']
};

export default {
  data: {
    almacenes: [],
    editingAlmacen: null,
    filters: { search: '', activo: '', tipo: '' },
    stats: {},
    responsables: [],
    distribuidores: []
  },

  async render() {
    const pp = permisosPantalla('almacenes');
    const formulario = `
      <div class="card erp-form-card" id="almFormCard">
        <div class="card-header">
          <h2 class="card-title" id="almFormTitulo">Nuevo almacén</h2>
          <span class="text-muted">El código (ALM-NN) lo pone el sistema.</span>
        </div>
        <form id="almacenForm" autocomplete="off">
          <div class="form-row">
            <div class="form-group" style="grid-column: span 2;">
              <label>Nombre *</label>
              <input type="text" id="nombre" class="form-control" maxlength="100" required placeholder="Ej: CONSIGNA AGROPARTES TEHUACÁN" />
            </div>
            <div class="form-group">
              <label>Tipo *</label>
              <select id="tipo" class="form-control">
                ${Object.entries(TIPOS).map(([k, [t]]) => `<option value="${k}">${t}</option>`).join('')}
              </select>
            </div>
            <div class="form-group" id="grupoCustodio" style="display:none;">
              <label>Custodio (distribuidor) *</label>
              <select id="custodio_persona_id" class="form-control"><option value="">— Elige el distribuidor —</option></select>
            </div>
          </div>
          <div class="form-row">
            <div class="form-group"><label>Ubicación</label><input type="text" id="ubicacion" class="form-control" maxlength="200" placeholder="Calle, número, referencia" /></div>
            <div class="form-group"><label>Ciudad</label><input type="text" id="ciudad" class="form-control" maxlength="100" /></div>
            <div class="form-group"><label>Estado</label><input type="text" id="estado" class="form-control" maxlength="60" /></div>
            <div class="form-group"><label>Teléfono</label><input type="text" id="telefono" class="form-control" maxlength="20" /></div>
          </div>
          <div class="form-row">
            <div class="form-group"><label>Responsable</label><select id="responsable_id" class="form-control"><option value="">Sin asignar</option></select></div>
            <div class="form-group"><label>Capacidad (unidades)</label><input type="number" id="capacidad" class="form-control" min="1" max="1000" /></div>
            <div class="form-group" style="display:flex;flex-direction:column;gap:.5rem;justify-content:flex-end;">
              <label class="erp-check"><input type="checkbox" id="permite_venta" checked /> Se puede vender desde aquí</label>
              <label class="erp-check"><input type="checkbox" id="es_principal" /> Es el almacén principal</label>
            </div>
          </div>
          <div class="form-group"><label>Descripción</label><input type="text" id="descripcion" class="form-control" maxlength="500" /></div>
          <div id="avisoTipo" class="erp-aviso erp-aviso-info" style="display:none;"></div>
          <div class="erp-acciones-form">
            <button type="button" class="btn btn-secondary" id="btnLimpiarForm">Cancelar</button>
            <button type="submit" class="btn btn-primary" id="btnGuardarAlmacen">Guardar almacén</button>
          </div>
        </form>
      </div>`;

    const consultar = `
      <div class="stats-grid" id="statsContainer"><div class="loading-spinner"></div></div>

      <div class="filters-card">
        <div class="filters-grid">
          <div class="form-group"><label>Buscar</label><input type="text" id="searchInput" placeholder="Código, nombre o ubicación..." class="form-control" /></div>
          <div class="form-group"><label>Tipo</label>
            <select id="tipoFilter" class="form-control"><option value="">Todos</option>${Object.entries(TIPOS).map(([k, [t]]) => `<option value="${k}">${t}</option>`).join('')}</select></div>
          <div class="form-group"><label>Estado</label>
            <select id="activoFilter" class="form-control"><option value="">Todos</option><option value="true">Activos</option><option value="false">Inactivos</option></select></div>
          <div class="form-group" style="display:flex;align-items:flex-end;"><button class="btn btn-secondary" id="btnLimpiarFiltros">Limpiar</button></div>
        </div>
      </div>

      <div class="card">
        <div class="table-container">
          <table class="table">
            <thead>
              <tr>
                <th>Código</th>
                <th>Nombre</th>
                <th>Tipo</th>
                <th class="tbl-hide-mobile">Ciudad</th>
                <th class="tbl-hide-mobile">Custodio / responsable</th>
                <th class="tbl-hide-mobile">Venta</th>
                <th>Estado</th>
                <th style="text-align:right;">Acciones</th>
              </tr>
            </thead>
            <tbody id="almacenesTableBody">
              <tr><td colspan="8" class="text-center"><div class="loading-spinner"></div><p>Cargando almacenes...</p></td></tr>
            </tbody>
          </table>
        </div>
      </div>
    `;
    return pestanasHtml({ id: 'alm', titulo: 'Catálogo de Almacenes', ...pp, consultar, formulario });
  },

  async afterRender() {
    if (!permisos.canView('almacenes')) {
      if (permisos.primeraRuta) { window.router.navigate(permisos.primeraRuta); }
      else { document.getElementById('content').innerHTML = '<div style="display:flex;justify-content:center;align-items:center;height:60vh;"><div style="text-align:center;color:var(--primary);"><h2>Acceso denegado</h2><p>No tienes permisos para acceder a este módulo.</p></div></div>'; }
      return;
    }
    window.currentView = this;
    this.data.editingAlmacen = null;
    this.tabs = new Pestanas('alm', { alAgregar: () => this.limpiarForm() });
    this.setupEventListeners();
    await Promise.all([this.cargarEstadisticas(), this.cargarAlmacenes(), this._cargarResponsables(), this._cargarDistribuidores()]);
  },

  setupEventListeners() {
    document.getElementById('almacenForm')?.addEventListener('submit', (e) => { e.preventDefault(); this.guardarAlmacen(); });
    document.getElementById('btnLimpiarForm')?.addEventListener('click', () => { this.limpiarForm(); this.tabs.volver(); });
    document.getElementById('tipo')?.addEventListener('change', () => this.alCambiarTipo());
    let t;
    document.getElementById('searchInput')?.addEventListener('input', (e) => {
      clearTimeout(t);
      t = setTimeout(() => { this.data.filters.search = e.target.value; this.cargarAlmacenes(); }, 300);
    });
    document.getElementById('activoFilter')?.addEventListener('change', (e) => { this.data.filters.activo = e.target.value; this.cargarAlmacenes(); });
    document.getElementById('tipoFilter')?.addEventListener('change', (e) => { this.data.filters.tipo = e.target.value; this.cargarAlmacenes(); });
    document.getElementById('btnLimpiarFiltros')?.addEventListener('click', () => this.limpiarFiltros());
  },

  alCambiarTipo() {
    const tipo = val('tipo');
    const grupo = document.getElementById('grupoCustodio');
    const aviso = document.getElementById('avisoTipo');
    const venta = document.getElementById('permite_venta');
    const principal = document.getElementById('es_principal');
    if (grupo) grupo.style.display = tipo === 'consigna' ? '' : 'none';
    const textos = {
      consigna: 'Consigna: la mercancía es de AgroMX pero la tiene un distribuidor. El custodio tiene que ser una persona con rol <strong>distribuidor</strong>.',
      transito: 'Tránsito: almacén virtual del traspaso en dos fases (sale del origen → queda aquí → alguien confirma la entrada). No se vende desde aquí y no puede ser el principal.',
      garantias: 'Garantías: piezas o unidades en revisión por garantía.',
      demo: 'Demo: unidades de demostración.',
      interno: 'Interno: herramienta y consumo interno.'
    };
    if (aviso) {
      aviso.style.display = textos[tipo] ? 'block' : 'none';
      aviso.innerHTML = textos[tipo] || '';
    }
    if (tipo === 'transito') {
      venta.checked = false; venta.disabled = true;
      principal.checked = false; principal.disabled = true;
    } else {
      venta.disabled = false; principal.disabled = false;
    }
  },

  async cargarEstadisticas() {
    try {
      const response = await almacenService.obtenerEstadisticas();
      const s = response.data.stats || {};
      const container = document.getElementById('statsContainer');
      if (!container) return;
      container.innerHTML = `
        <div class="stat-card"><div class="stat-icon">📊</div><div class="stat-content"><div class="stat-value">${s.total || 0}</div><div class="stat-label">Total Almacenes</div></div></div>
        <div class="stat-card stat-success"><div class="stat-icon">✅</div><div class="stat-content"><div class="stat-value">${s.activos || 0}</div><div class="stat-label">Activos</div></div></div>
        <div class="stat-card stat-warning"><div class="stat-icon">⏸️</div><div class="stat-content"><div class="stat-value">${s.inactivos || 0}</div><div class="stat-label">Inactivos</div></div></div>
        <div class="stat-card stat-info"><div class="stat-icon">📦</div><div class="stat-content"><div class="stat-value">${s.capacidad_total || 0}</div><div class="stat-label">Capacidad Total</div></div></div>
      `;
    } catch (error) {
      console.error('Error al cargar estadísticas:', error);
    }
  },

  async cargarAlmacenes() {
    const tbody = document.getElementById('almacenesTableBody');
    try {
      const response = await almacenService.listar(this.data.filters);
      this.data.almacenes = response.data.almacenes || [];
      this.renderTabla();
    } catch (error) {
      if (tbody) tbody.innerHTML = `<tr><td colspan="8" class="text-center" style="color:var(--danger);">Error al cargar almacenes: ${esc(errMsg(error))}</td></tr>`;
    }
  },

  renderTabla() {
    const tbody = document.getElementById('almacenesTableBody');
    if (!tbody) return;
    if (!this.data.almacenes.length) {
      tbody.innerHTML = '<tr><td colspan="8" class="text-center">No se encontraron almacenes</td></tr>';
      return;
    }
    tbody.innerHTML = this.data.almacenes.map((a) => {
      const [tTxt, tCls] = TIPOS[a.tipo] || [a.tipo || '—', 'info'];
      const esTransito = a.tipo === 'transito';
      return `
        <tr ${!a.activo ? 'style="opacity:.6;"' : ''}>
          <td><strong>${esc(a.codigo)}</strong>${a.es_principal ? '<div><span class="erp-chip erp-chip-oblig">principal</span></div>' : ''}</td>
          <td>${esc(a.nombre)}${a.ubicacion ? `<div class="text-muted" style="font-size:.75rem;">${esc(a.ubicacion)}</div>` : ''}</td>
          <td>${badge(tTxt, tCls)}</td>
          <td class="tbl-hide-mobile">${esc([a.ciudad, a.estado].filter(Boolean).join(', ')) || '<span class="text-muted">—</span>'}</td>
          <td class="tbl-hide-mobile" style="font-size:.85rem;">${a.tipo === 'consigna' ? `<strong>${esc(a.custodio_nombre || '—')}</strong>` : esc(a.responsable_nombre || 'Sin asignar')}</td>
          <td class="tbl-hide-mobile">${a.permite_venta ? 'Sí' : '<span class="text-muted">No</span>'}</td>
          <td>${a.activo ? badge('Activo', 'success') : badge('Inactivo', 'warning')}</td>
          <td style="text-align:right;white-space:nowrap;">
            ${permisos.canEdit('almacenes') ? `<button class="btn-icon btn-edit" onclick="window.currentView.editarAlmacen(${a.id})" title="Editar">✏️</button>` : ''}
            ${permisos.can('almacenes', 'cambiar_estado') && !esTransito ? `<button class="btn-icon ${a.activo ? 'btn-warning' : 'btn-success'}" onclick="window.currentView.toggleActivo(${a.id})" title="${a.activo ? 'Desactivar' : 'Activar'}">${a.activo ? '⏸️' : '▶️'}</button>` : ''}
            ${permisos.canDelete('almacenes') && !esTransito && !a.es_principal ? `<button class="btn-icon btn-danger" onclick="window.currentView.eliminarAlmacen(${a.id})" title="Dar de baja">🗑️</button>` : ''}
          </td>
        </tr>`;
    }).join('');
  },

  async _cargarResponsables() {
    const select = document.getElementById('responsable_id');
    if (!select) return;
    try {
      const response = await api.get('/usuarios/responsables');
      const todos = Array.isArray(response.data) ? response.data : (response.data?.usuarios || response.usuarios || []);
      this.data.responsables = todos.filter((u) => u.persona_id);
      select.innerHTML = '<option value="">Sin asignar</option>' + this.data.responsables
        .map((u) => `<option value="${u.id}">${esc(u.persona_nombre_completo || u.nombre_completo)}</option>`).join('');
    } catch (error) {
      select.innerHTML = '<option value="">Sin asignar</option>';
    }
  },

  async _cargarDistribuidores() {
    const select = document.getElementById('custodio_persona_id');
    if (!select) return;
    try {
      const res = await api.get('/personas/rol/distribuidor');
      this.data.distribuidores = res?.data?.personas || [];
      select.innerHTML = '<option value="">— Elige el distribuidor —</option>' + this.data.distribuidores
        .map((p) => `<option value="${p.id}">${esc(p.nombre_completo)}${p.ciudad ? ` · ${esc(p.ciudad)}` : ''}</option>`).join('');
    } catch (error) {
      select.innerHTML = '<option value="">No se pudieron cargar los distribuidores</option>';
    }
  },

  limpiarForm() {
    this.data.editingAlmacen = null;
    document.getElementById('almacenForm')?.reset();
    document.getElementById('almFormTitulo').textContent = 'Nuevo almacén';
    document.getElementById('almFormCard')?.classList.remove('editando');
    document.getElementById('btnGuardarAlmacen').textContent = 'Guardar almacén';
    document.getElementById('tipo').disabled = false;
    this.alCambiarTipo();
  },

  async editarAlmacen(id) {
    try {
      const response = await almacenService.obtenerPorId(id);
      const a = response.data.almacen;
      this.data.editingAlmacen = a;
      document.getElementById('almFormTitulo').textContent = `Editar ${a.codigo} · ${a.nombre}`;
      document.getElementById('almFormCard')?.classList.add('editando');
      document.getElementById('btnGuardarAlmacen').textContent = 'Guardar cambios';
      const set = (i, v) => { const el = document.getElementById(i); if (el) el.value = v ?? ''; };
      set('nombre', a.nombre);
      set('tipo', a.tipo || 'propio');
      document.getElementById('tipo').disabled = a.tipo === 'transito';
      set('custodio_persona_id', a.custodio_persona_id);
      set('ubicacion', a.ubicacion);
      set('ciudad', a.ciudad);
      set('estado', a.estado);
      set('telefono', a.telefono);
      set('responsable_id', a.responsable_id);
      set('capacidad', a.capacidad);
      set('descripcion', a.descripcion);
      document.getElementById('permite_venta').checked = !!a.permite_venta;
      document.getElementById('es_principal').checked = !!a.es_principal;
      this.alCambiarTipo();
      this.tabs.abrirFormulario({ editando: true, etiqueta: a.codigo });
    } catch (error) {
      toast.error('Error al cargar almacén');
    }
  },

  async guardarAlmacen() {
    const tipo = val('tipo');
    const responsableVal = val('responsable_id');
    const formData = {
      nombre: val('nombre'),
      descripcion: val('descripcion') || null,
      ubicacion: val('ubicacion') || null,
      capacidad: parseInt(val('capacidad'), 10) || null,
      responsable_id: responsableVal ? parseInt(responsableVal, 10) : null,
      telefono: val('telefono') || null,
      tipo,
      custodio_persona_id: tipo === 'consigna' && val('custodio_persona_id') ? Number(val('custodio_persona_id')) : null,
      es_principal: val('es_principal'),
      permite_venta: val('permite_venta'),
      ciudad: val('ciudad') || null,
      estado: val('estado') || null
    };
    if (!formData.nombre) { toast.warning('El nombre es obligatorio'); return; }
    if (tipo === 'consigna' && !formData.custodio_persona_id) { toast.warning('Una consigna necesita su distribuidor custodio'); return; }
    try {
      if (this.data.editingAlmacen) {
        await almacenService.actualizar(this.data.editingAlmacen.id, formData);
        toast.success('Almacén actualizado');
      } else {
        await almacenService.crear(formData);
        toast.success('Almacén creado');
      }
      this.limpiarForm();
      this.tabs.volver();
      await Promise.all([this.cargarAlmacenes(), this.cargarEstadisticas()]);
    } catch (error) {
      toast.error(errMsg(error, 'Error al guardar almacén'));
    }
  },

  toggleActivo(id) {
    const a = this.data.almacenes.find((x) => x.id === id);
    const accion = a?.activo ? 'desactivar' : 'activar';
    modal.confirm('Cambiar estado del almacén', `¿Está seguro de ${accion} a <strong>${esc(a?.nombre || 'este almacén')}</strong>?`, async () => {
      try {
        await almacenService.toggleActivo(id);
        toast.success('Estado actualizado');
        await Promise.all([this.cargarAlmacenes(), this.cargarEstadisticas()]);
      } catch (error) {
        toast.error(errMsg(error, 'Error al cambiar estado'));
      }
    });
  },

  eliminarAlmacen(id) {
    const a = this.data.almacenes.find((x) => x.id === id);
    modal.confirm('Dar de baja el almacén', `¿Dar de baja <strong>${esc(a?.nombre || 'este almacén')}</strong>? <p class="text-muted">No se borra: sale del catálogo. Si tiene unidades, no se permite.</p>`, async () => {
      try {
        await almacenService.eliminar(id);
        toast.success('Almacén dado de baja');
        await Promise.all([this.cargarAlmacenes(), this.cargarEstadisticas()]);
      } catch (error) {
        toast.error(errMsg(error, 'Error al dar de baja'));
      }
    });
  },

  limpiarFiltros() {
    this.data.filters = { search: '', activo: '', tipo: '' };
    document.getElementById('searchInput').value = '';
    document.getElementById('activoFilter').value = '';
    document.getElementById('tipoFilter').value = '';
    this.cargarAlmacenes();
  }
};
